import { processAmendment } from '../../shared/amendmentHelpers';
import { buildQuotationWithUpdatedBenefits, validateMemberBenefits } from './quotationBuilder';
import { getAddedBenefits, getRemovedBenefits } from '../utils/benefitHelpers';

/**
 * Build policy note for benefit changes
 * @param {Object} policy - Policy object
 * @param {Array} originalMembers - Original members
 * @param {Array} updatedMembers - Updated members
 * @returns {String} - Formatted policy note
 */
function buildBenefitChangeNote(policy, originalMembers, updatedMembers) {
  const oldPremium = policy.quotation.policy_schedule_entries
    .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;

  const newPremium = updatedMembers
    .reduce((sum, member) => sum + (member.premium_amount_in_cents || 0), 0) / 100;

  const premiumDifference = newPremium - oldPremium;
  const changeType = premiumDifference > 0 ? 'Increase' : (premiumDifference < 0 ? 'Decrease' : 'No Change');

  // Count changes
  let totalAdded = 0;
  let totalRemoved = 0;

  updatedMembers.forEach(updatedMember => {
    const original = originalMembers.find(m => m.uuid === updatedMember.uuid);
    if (!original) return;

    const originalBenefits = original.benefits || [];
    const newBenefits = updatedMember.benefits || [];

    const added = getAddedBenefits(originalBenefits, newBenefits);
    const removed = getRemovedBenefits(originalBenefits, newBenefits);

    totalAdded += added.length;
    totalRemoved += removed.length;
  });

  // Build note
  const noteContent = `BENEFIT AMENDMENT

Policy: ${policy.policy_number}

Changes:
Added: ${totalAdded} benefit(s)
Removed: ${totalRemoved} benefit(s)

Premium:
Old: BWP ${oldPremium.toFixed(2)}
New: BWP ${newPremium.toFixed(2)}
${changeType}: BWP ${Math.abs(premiumDifference).toFixed(2)}

Members: ${updatedMembers.length}`;

  return noteContent;
}

/**
 * Processes the Manage Benefits amendment
 * @param {Object} policy - The current policy object
 * @param {Array} updatedMembers - Members with updated benefits
 * @param {Function} updateStatus - Callback to update status messages
 * @returns {Promise} - Resolves with success message or rejects with error
 */
export async function processManageBenefitsAmendment(policy, updatedMembers, updateStatus) {
  try {
    // Validate all members have required benefits
    const validation = validateMemberBenefits(updatedMembers);
    if (!validation.valid) {
      throw new Error(validation.errors.join(', '));
    }

    // Extract original members for comparison
    const originalMembers = policy.quotation.policy_schedule_entries.map(entry => {
      if (entry.dependant) {
        return {
          ...entry.dependant,
          benefits: entry.benefits || [],
          premium_amount_in_cents: entry.premium_amount_in_cents || 0,
          uuid: entry.dependant.additional_details && entry.dependant.additional_details.uuid
            ? entry.dependant.additional_details.uuid
            : entry.dependant.guid
        };
      } else {
        return {
          ...entry,
          uuid: entry.uuid || entry.guid
        };
      }
    });

    // Build quotation payload
    const quotationPayload = buildQuotationWithUpdatedBenefits(policy, updatedMembers);

    // Build policy note
    const noteContent = buildBenefitChangeNote(policy, originalMembers, updatedMembers);

    // Use shared amendment helper
    const result = await processAmendment({
      quotationPayload,
      policy,
      noteContent,
      updateStatus
    });

    return result;
  } catch (error) {
    console.error('Error processing manage benefits amendment:', error);
    throw new Error(
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.message || 'Failed to process benefit amendment. Please try again.'
    );
  }
}
